import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  CircularProgress,
  Snackbar,
  SnackbarContent,
} from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      minWidth: 400,
      [theme.breakpoints.down('xs')]: {
        minWidth: '90vw',
        margin: theme.spacing(2),
      },
    },
  },
  warningText: {
    color: theme.palette.error.main,
    fontWeight: 500,
    marginBottom: theme.spacing(2),
  },
  confirmationText: {
    marginBottom: theme.spacing(2),
  },
  textField: {
    marginBottom: theme.spacing(2),
  },
  alert: {
    marginBottom: theme.spacing(2),
  },
  loadingBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
  },
  error: {
    backgroundColor: theme.palette.error.dark,
  },
  success: {
    backgroundColor: theme.palette.success.dark,
  },
  message: {
    display: 'flex',
    alignItems: 'center',
  },
}));

const DeleteAccountModal = ({ open, onClose, onConfirm, loading = false }) => {
  const classes = useStyles();
  const [confirmationText, setConfirmationText] = useState('');
  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState('error');

  const handleConfirm = async () => {
    if (confirmationText !== 'DELETE') {
      setError('Please type DELETE to confirm account deletion');
      return;
    }
    setError('');
    
    try {
      await onConfirm();
      // If we reach here, the account was deleted successfully
      setSnackbarMessage('Account deleted successfully');
      setSnackbarType('success');
      setSnackbarOpen(true);
      // Close modal after showing success message
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      setSnackbarMessage(error.message || 'Failed to delete account');
      setSnackbarType('error');
      setSnackbarOpen(true);
    }
  };

  const handleClose = () => {
    setConfirmationText('');
    setError('');
    setSnackbarOpen(false);
    onClose();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        className={classes.dialog}
        maxWidth="sm"
        fullWidth
      >
      <DialogTitle>
          ⚠️ Delete Account
      </DialogTitle>
      <DialogContent>
        <Alert severity="error" className={classes.alert}>
          <AlertTitle>⚠️ Account Deletion Warning</AlertTitle>
          <Typography variant="body2" style={{ marginBottom: '8px' }}>
             Deleting your account
            will permanently remove your online account within the Simple Order System.
          </Typography>
         
        </Alert>

        <Typography variant="body2" className={classes.confirmationText}>
          To confirm account deletion, please type <strong>DELETE</strong> in the
          field below (all caps) and click the button below:
        </Typography>

        <TextField
          fullWidth
          variant="outlined"
          placeholder="Type DELETE to confirm"
          value={confirmationText}
          onChange={(e) => setConfirmationText(e.target.value)}
          className={classes.textField}
          error={!!error}
          helperText={error}
        />

        {loading && (
          <Box className={classes.loadingBox}>
            <CircularProgress size={20} />
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginLeft: '8px' }}
            >
              Deleting account...
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="secondary"
          variant="contained"
          disabled={loading || confirmationText !== 'DELETE'}
        >
          Delete Account
        </Button>
      </DialogActions>
    </Dialog>
    <Snackbar
      open={snackbarOpen}
      autoHideDuration={6000}
      onClose={handleSnackbarClose}
    >
      <SnackbarContent
        className={classes[snackbarType]}
        message={
          <span className={classes.message}>{snackbarMessage}</span>
        }
      />
    </Snackbar>
    </>
  );
};

export default DeleteAccountModal; 